/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  Modal,
  TextInput,
  SectionList,
  ActivityIndicator,
  Alert,
  Switch,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
} from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import * as FileSystem from 'expo-file-system';
import NetInfo from '@react-native-community/netinfo';

// ==================== API CONFIGURATION ====================
const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  companyProfile: `${API_BASE_URL}/api/data/company_profiles`,
  contracts: `${API_BASE_URL}/api/data/contracts`,
  contractPayments: `${API_BASE_URL}/api/data/contract_payments`,
  contractMilestones: `${API_BASE_URL}/api/data/contract_milestones`,
};

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
  },
});

api.interceptors.request.use(
  async (config) => {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        config.params = { ...config.params, company_id: parseInt(companyId) };
      }
    } catch (error) {
      console.error('Error in request interceptor:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Key for locally stored contracts (fallback) and seeding flag
const LOCAL_CONTRACTS_KEY = 'local_contracts';
const SEED_FLAG_KEY = 'db_seeded';

// Helper to safely extract array from API response
const extractDataArray = (responseData) => {
  if (Array.isArray(responseData)) return responseData;
  if (responseData && Array.isArray(responseData.data)) return responseData.data;
  if (responseData && Array.isArray(responseData.records)) return responseData.records;
  if (responseData && Array.isArray(responseData.items)) return responseData.items;
  return [];
};

// ---------- MOCK DATA FOR SEEDING ----------
const MOCK_CONTRACTS = [
  {
    contract_name: 'Electrical Installation - Downtown Office',
    contract_type: 'Electrical',
    first_party_id: 1, // will be replaced
    second_party_name: 'Elite Electrical Services',
    effective_date: '2025-03-15',
    contract_value: 85000,
    currency: 'GBP',
    status: 'Active',
    metadata: JSON.stringify({
      projectId: 'proj1',
      projectName: 'Downtown Office Complex',
      vendor_id: 'sub1',
      milestones: [
        { id: 'ms1-1', title: 'Initial Wiring', amount: 25500, dueDate: '2025-04-20', status: 'Completed', paymentStatus: 'Paid', paymentDate: '2025-04-22', transactionHash: '0x8a7d56b8e3...f4e2' },
        { id: 'ms1-2', title: 'Panel Installation', amount: 34000, dueDate: '2025-06-10', status: 'In Progress', paymentStatus: 'Pending', completionPercentage: 65 },
        { id: 'ms1-3', title: 'Final Testing', amount: 25500, dueDate: '2025-07-25', status: 'Not Started', paymentStatus: 'Not Due' }
      ]
    })
  },
  {
    contract_name: 'Plumbing - Downtown Office',
    contract_type: 'Plumbing',
    first_party_id: 1,
    second_party_name: 'Modern Plumbing Co.',
    effective_date: '2025-03-20',
    contract_value: 72000,
    currency: 'GBP',
    status: 'Active',
    metadata: JSON.stringify({
      projectId: 'proj1',
      projectName: 'Downtown Office Complex',
      vendor_id: 'sub2',
      milestones: [
        { id: 'ms2-1', title: 'Rough-In Plumbing', amount: 21600, dueDate: '2025-04-25', status: 'Completed', paymentStatus: 'Paid', paymentDate: '2025-04-28', transactionHash: '0x9b8c45a7d2...e3f1' },
        { id: 'ms2-2', title: 'Fixture Installation', amount: 28800, dueDate: '2025-06-05', status: 'Completed', paymentStatus: 'Processing', completionEvidence: 'https://jobeng.com/evidence/ms2-2' },
        { id: 'ms2-3', title: 'Testing & Finalization', amount: 21600, dueDate: '2025-07-10', status: 'Not Started', paymentStatus: 'Not Due' }
      ]
    })
  },
  {
    contract_name: 'Foundation - Riverside Apartments',
    contract_type: 'Foundation',
    first_party_id: 1,
    second_party_name: 'Foundation Masters',
    effective_date: '2025-02-10',
    contract_value: 120000,
    currency: 'GBP',
    status: 'On Hold',
    metadata: JSON.stringify({
      projectId: 'proj2',
      projectName: 'Riverside Apartments',
      vendor_id: 'sub3',
      milestones: [
        { id: 'ms3-1', title: 'Site Preparation', amount: 36000, dueDate: '2025-03-01', status: 'Completed', paymentStatus: 'Paid', paymentDate: '2025-03-05', transactionHash: '0x7c6d45b8a9...d2e3' },
        { id: 'ms3-2', title: 'Foundation Pouring', amount: 48000, dueDate: '2025-04-15', status: 'Completed', paymentStatus: 'Paid', paymentDate: '2025-04-20', transactionHash: '0x5a4b8c9d7e...a1b2' },
        { id: 'ms3-3', title: 'Curing & Waterproofing', amount: 36000, dueDate: '2025-05-25', status: 'On Hold', paymentStatus: 'On Hold', holdReason: 'Project timeline adjustment' }
      ]
    })
  },
  {
    contract_name: 'Steel Structure - Industrial Park',
    contract_type: 'Structural Steel',
    first_party_id: 1,
    second_party_name: 'Steel Structure Specialists',
    effective_date: '2025-04-01',
    contract_value: 195000,
    currency: 'GBP',
    status: 'Active',
    metadata: JSON.stringify({
      projectId: 'proj3',
      projectName: 'Industrial Park Expansion',
      vendor_id: 'sub4',
      milestones: [
        { id: 'ms4-1', title: 'Material Procurement', amount: 58500, dueDate: '2025-04-30', status: 'Completed', paymentStatus: 'Paid', paymentDate: '2025-05-03', transactionHash: '0x3d4e5f6a7b...c8d9' },
        { id: 'ms4-2', title: 'Frame Installation', amount: 78000, dueDate: '2025-06-30', status: 'In Progress', paymentStatus: 'Pending', completionPercentage: 40 },
        { id: 'ms4-3', title: 'Welding & Finishing', amount: 58500, dueDate: '2025-08-10', status: 'Not Started', paymentStatus: 'Not Due' }
      ]
    })
  }
];

const MOCK_PAYMENTS = [
  {
    id: 'pay1',
    contractId: 'contract1',
    milestoneId: 'ms1-1',
    projectName: 'Downtown Office Complex',
    subcontractorName: 'Elite Electrical Services',
    amount: 25500,
    date: '2025-04-22',
    status: 'Completed',
    transactionHash: '0x8a7d56b8e3...f4e2',
    gasUsed: 0.0032,
    networkFee: 0.25,
  },
  {
    id: 'pay2',
    contractId: 'contract2',
    milestoneId: 'ms2-1',
    projectName: 'Downtown Office Complex',
    subcontractorName: 'Modern Plumbing Co.',
    amount: 21600,
    date: '2025-04-28',
    status: 'Completed',
    transactionHash: '0x9b8c45a7d2...e3f1',
    gasUsed: 0.0028,
    networkFee: 0.22,
  },
  {
    id: 'pay3',
    contractId: 'contract3',
    milestoneId: 'ms3-1',
    projectName: 'Riverside Apartments',
    subcontractorName: 'Foundation Masters',
    amount: 36000,
    date: '2025-03-05',
    status: 'Completed',
    transactionHash: '0x7c6d45b8a9...d2e3',
    gasUsed: 0.0035,
    networkFee: 0.28,
  },
  {
    id: 'pay4',
    contractId: 'contract3',
    milestoneId: 'ms3-2',
    projectName: 'Riverside Apartments',
    subcontractorName: 'Foundation Masters',
    amount: 48000,
    date: '2025-04-20',
    status: 'Completed',
    transactionHash: '0x5a4b8c9d7e...a1b2',
    gasUsed: 0.004,
    networkFee: 0.32,
  },
  {
    id: 'pay5',
    contractId: 'contract4',
    milestoneId: 'ms4-1',
    projectName: 'Industrial Park Expansion',
    subcontractorName: 'Steel Structure Specialists',
    amount: 58500,
    date: '2025-05-03',
    status: 'Completed',
    transactionHash: '0x3d4e5f6a7b...c8d9',
    gasUsed: 0.0045,
    networkFee: 0.36,
  },
];

const MOCK_PENDING = [
  {
    id: 'pending1',
    contractId: 'contract2',
    milestoneId: 'ms2-2',
    projectName: 'Downtown Office Complex',
    subcontractorName: 'Modern Plumbing Co.',
    milestoneTitle: 'Fixture Installation',
    amount: 28800,
    dueDate: '2025-06-05',
    status: 'Processing',
    submissionDate: '2025-06-02',
    approvalStatus: 'Approved',
    approvalDate: '2025-06-03',
    estimatedCompletionDate: '2025-06-06',
  },
  {
    id: 'pending2',
    contractId: 'contract1',
    milestoneId: 'ms1-2',
    projectName: 'Downtown Office Complex',
    subcontractorName: 'Elite Electrical Services',
    milestoneTitle: 'Panel Installation',
    amount: 34000,
    dueDate: '2025-06-10',
    status: 'Pending Approval',
    submissionDate: '2025-06-01',
    approvalStatus: 'Under Review',
    completionPercentage: 65,
  },
  {
    id: 'pending3',
    contractId: 'contract4',
    milestoneId: 'ms4-2',
    projectName: 'Industrial Park Expansion',
    subcontractorName: 'Steel Structure Specialists',
    milestoneTitle: 'Frame Installation',
    amount: 78000,
    dueDate: '2025-06-30',
    status: 'Not Submitted',
    completionPercentage: 40,
  },
];

const SmartContractPaymentScreen = ({ navigation }) => {
  const [activeTab, setActiveTab] = useState('Contracts');
  const [selectedContract, setSelectedContract] = useState(null);
  const [showContractModal, setShowContractModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedPendingPayment, setSelectedPendingPayment] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showWalletModal, setShowWalletModal] = useState(false);

  // Data fetched from API
  const [contracts, setContracts] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOffline, setIsOffline] = useState(false);

  // Settings state
  const [settings, setSettings] = useState({
    autoApproveThreshold: 25000,
    requireMultiSigAbove: 50000,
    notifyOnMilestoneApproach: true,
    defaultGasPrice: 'Standard',
    paymentCurrency: 'ETH',
  });

  // Wallet mock data (to be replaced with real data)
  const [walletBalance, setWalletBalance] = useState(245000);
  const [walletAddress, setWalletAddress] = useState('0x7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c2d3');
  const [networkGasPrice, setNetworkGasPrice] = useState(0.00000025);
  const [networkCongestion, setNetworkCongestion] = useState('Low');

  // State for company logo
  const [logoUrl, setLogoUrl] = useState(null);

  // Load all data on mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const netState = await NetInfo.fetch();
      if (!netState.isConnected) {
        setIsOffline(true);
        await loadLocalContracts();
        setLoading(false);
        return;
      }

      try {
        await loadCompanyLogo();
        await Promise.all([
          fetchContracts(),
          fetchPaymentHistory(),
          fetchPendingPayments(),
        ]);
      } catch (error) {
        console.error('Error loading data:', error);
        Alert.alert('Error', 'Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const loadCompanyLogo = async () => {
    try {
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        setLogoUrl(profile.logo || null);
      }

      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        const response = await api.get(API_ENDPOINTS.companyProfile, {
          params: { company_id: parseInt(companyId) },
        });
        if (response.data?.success && response.data.data && response.data.data.length > 0) {
          const latestProfile = response.data.data[0];
          setLogoUrl(latestProfile.logo || null);
          await AsyncStorage.setItem('company_profile', JSON.stringify(latestProfile));
        }
      }
    } catch (error) {
      console.warn('Error loading company logo:', error);
    }
  };

  const fetchContracts = async () => {
    try {
      const response = await api.get(API_ENDPOINTS.contracts);
      const contractsArray = extractDataArray(response.data);
      // Parse metadata for each contract
      const parsed = contractsArray.map(contract => {
        let c = { ...contract };
        if (c.metadata) {
          try { const m = typeof c.metadata === 'string' ? JSON.parse(c.metadata) : c.metadata; c = { ...c, ...m }; } catch {}
        }
        // Normalize fields
        c.totalAmount = parseFloat(c.totalAmount || c.contract_value || c.total_amount || c.value || 0);
        c.contractName = c.contractName || c.contract_name || c.title || c.name || 'Untitled';
        c.contractNumber = c.contractNumber || c.contract_number || '';
        c.contractType = c.contractType || c.contract_type || 'general';
        c.startDate = c.startDate || c.start_date || c.effective_date || '';
        c.endDate = c.endDate || c.end_date || '';
        c.secondPartyName = c.secondPartyName || c.second_party_name || c.counterparty || c.client_name || '';
        c.status = (c.status || 'draft').toLowerCase();
        c.milestones = Array.isArray(c.milestones) ? c.milestones : [];
        c.milestones = c.milestones.map(m => ({ ...m, amount: parseFloat(m.amount || 0) }));
        return c;
      });
      setContracts(parsed);

      // If contracts are empty, seed the database
      if (parsed.length === 0 && !isOffline) {
        await seedDatabaseIfNeeded();
        // Refetch after seeding
        const secondResponse = await api.get(API_ENDPOINTS.contracts);
        const secondArray = extractDataArray(secondResponse.data);
        const secondParsed = secondArray.map(contract => {
          let c = { ...contract };
          if (c.metadata) { try { const m = typeof c.metadata === 'string' ? JSON.parse(c.metadata) : c.metadata; c = { ...c, ...m }; } catch {} }
          c.totalAmount = parseFloat(c.totalAmount || c.contract_value || 0);
          c.contractName = c.contractName || c.contract_name || c.title || c.name || 'Untitled';
          c.contractNumber = c.contractNumber || c.contract_number || '';
          c.startDate = c.startDate || c.start_date || c.effective_date || '';
          c.endDate = c.endDate || c.end_date || '';
          c.secondPartyName = c.secondPartyName || c.second_party_name || '';
          c.status = (c.status || 'draft').toLowerCase();
          c.milestones = Array.isArray(c.milestones) ? c.milestones.map(m => ({ ...m, amount: parseFloat(m.amount || 0) })) : [];
          return c;
        });
        setContracts(secondParsed);
      }
    } catch (error) {
      console.error('Failed to fetch contracts, loading local', error);
      await loadLocalContracts();
    }
  };

  const seedDatabaseIfNeeded = async () => {
    try {
      const seeded = await AsyncStorage.getItem(SEED_FLAG_KEY);
      if (seeded === 'true') return;

      const netState = await NetInfo.fetch();
      if (!netState.isConnected) return;

      const companyId = await AsyncStorage.getItem('company_id');
      if (!companyId) return;

      // Seed contracts
      for (const contract of MOCK_CONTRACTS) {
        const payload = { ...contract, first_party_id: parseInt(companyId) };
        await api.post(API_ENDPOINTS.contracts, payload).catch(e => console.warn('Contract seed failed', e));
      }

      // Seed payments (if endpoint exists)
      for (const payment of MOCK_PAYMENTS) {
        await api.post(API_ENDPOINTS.contractPayments, payment).catch(() => {});
      }

      // Seed pending payments
      for (const pending of MOCK_PENDING) {
        await api.post(API_ENDPOINTS.contractMilestones, pending).catch(() => {});
      }

      await AsyncStorage.setItem(SEED_FLAG_KEY, 'true');
    } catch (error) {
      console.error('Seeding error:', error);
    }
  };

  const loadLocalContracts = async () => {
    try {
      const local = await AsyncStorage.getItem(LOCAL_CONTRACTS_KEY);
      if (local) {
        const parsed = JSON.parse(local);
        const formatted = parsed.map(c => ({
          id: c.id,
          projectName: c.metadata?.projectName || 'Local Contract',
          subcontractorName: c.second_party_name,
          contractType: c.contract_type,
          totalAmount: c.contract_value,
          startDate: c.effective_date,
          endDate: c.metadata?.end_date,
          status: c.status,
          milestones: c.metadata?.milestones || [],
        }));
        setContracts(formatted);
      }
    } catch (error) {
      console.error('Failed to load local contracts', error);
    }
  };

  const normalizePayment = (p) => ({
    ...p,
    amount: parseFloat(p.amount || p.payment_amount || p.total || 0),
    date: p.date || p.payment_date || p.created_at || '',
    description: p.description || p.notes || '',
    status: (p.status || 'completed').toLowerCase(),
    contractName: p.contractName || p.contract_name || '',
  });

  const fetchPaymentHistory = async () => {
    try {
      const response = await api.get(API_ENDPOINTS.contractPayments);
      setPaymentHistory(extractDataArray(response.data));
    } catch (error) {
      console.warn('Failed to fetch payment history, using mock data');
      setPaymentHistory(MOCK_PAYMENTS);
    }
  };

  const fetchPendingPayments = async () => {
    try {
      const response = await api.get(API_ENDPOINTS.contractMilestones, {
        params: { status: 'pending' },
      });
      setPendingPayments(extractDataArray(response.data));
    } catch (error) {
      console.warn('Failed to fetch pending payments, using mock data');
      setPendingPayments(MOCK_PENDING);
    }
  };

  // Helper function to format currency
  const formatCurrency = (amount) => {
    try {
      const num = parseFloat(amount) || 0;
      return '\u00A3' + num.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    } catch { return '\u00A30.00'; }
  };

  // Helper function to get status color
  const getStatusColor = status => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'completed':
      case 'paid':
        return '#10B981';
      case 'in progress':
      case 'processing':
      case 'pending':
      case 'pending approval':
        return '#FBBF24';
      case 'on hold':
        return '#3B82F6';
      case 'not started':
      case 'not due':
      case 'not submitted':
      case 'draft':
        return '#64748B';
      default:
        return '#64748B';
    }
  };

  // Filter functions
  const getFilteredContracts = () => {
    if (!searchQuery) return contracts;
    return contracts.filter(
      contract =>
        (contract.projectName && String(contract.projectName ?? '').toLowerCase().includes(searchQuery.toLowerCase())) ||
        (contract.subcontractorName && String(contract.subcontractorName ?? '').toLowerCase().includes(searchQuery.toLowerCase())) ||
        (contract.contractType && String(contract.contractType ?? '').toLowerCase().includes(searchQuery.toLowerCase()))
    );
  };

  const getFilteredPayments = () => {
    if (!searchQuery) return paymentHistory;
    return paymentHistory.filter(
      payment =>
        (payment.projectName && String(payment.projectName ?? '').toLowerCase().includes(searchQuery.toLowerCase())) ||
        (payment.subcontractorName && String(payment.subcontractorName ?? '').toLowerCase().includes(searchQuery.toLowerCase()))
    );
  };

  const getFilteredPendingPayments = () => {
    if (!searchQuery) return pendingPayments;
    return pendingPayments.filter(
      payment =>
        (payment.projectName && String(payment.projectName ?? '').toLowerCase().includes(searchQuery.toLowerCase())) ||
        (payment.subcontractorName && String(payment.subcontractorName ?? '').toLowerCase().includes(searchQuery.toLowerCase())) ||
        (payment.milestoneTitle && String(payment.milestoneTitle ?? '').toLowerCase().includes(searchQuery.toLowerCase()))
    );
  };

  // Export functions
  const generateContractHTML = (contract) => {
    return `
      <html>
        <head>
          <style>
            body { font-family: Arial; padding: 20px; }
            h1 { color: #0e4e96; }
            .section { margin-bottom: 20px; }
            .label { font-weight: bold; }
          </style>
        </head>
        <body>
          <h1>Contract: ${contract.projectName || contract.contract_name}</h1>
          <div class="section">
            <p><span class="label">Type:</span> ${contract.contractType || contract.contract_type}</p>
            <p><span class="label">Subcontractor:</span> ${contract.subcontractorName || contract.second_party_name}</p>
            <p><span class="label">Total Amount:</span> ${formatCurrency(contract.totalAmount || contract.contract_value)}</p>
            <p><span class="label">Start Date:</span> ${contract.startDate || contract.effective_date}</p>
            <p><span class="label">End Date:</span> ${contract.endDate || contract.metadata?.end_date}</p>
            <p><span class="label">Status:</span> ${contract.status}</p>
          </div>
          ${contract.milestones ? `
          <div class="section">
            <h2>Milestones</h2>
            <ul>
              ${contract.milestones.map(m => `<li>${m.title} - ${formatCurrency(m.amount)} (${m.status || 'Pending'})</li>`).join('')}
            </ul>
          </div>
          ` : ''}
        </body>
      </html>
    `;
  };

  const handlePrintContract = async (contract) => {
    try {
      const html = generateContractHTML(contract);
      await Print.printAsync({ html });
    } catch (error) {
      Alert.alert('Error', 'Failed to print contract');
    }
  };

  const handleExportPDF = async (contract) => {
    try {
      const html = generateContractHTML(contract);
      const { uri } = await Print.printToFileAsync({ html });
      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(uri);
      } else {
        Alert.alert('Saved', `PDF saved to ${uri}`);
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to generate PDF');
    }
  };

  const handleExportWord = async (contract) => {
    try {
      const html = generateContractHTML(contract);
      const fileUri = FileSystem.documentDirectory + `contract_${contract.id}.doc`;
      await FileSystem.writeAsStringAsync(fileUri, html, { encoding: FileSystem.EncodingType.UTF8 });
      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(fileUri);
      } else {
        Alert.alert('Saved', `Word document saved to ${fileUri}`);
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to export Word document');
    }
  };

  // Handlers
  const handleContractPress = contract => {
    setSelectedContract(contract);
    setShowContractModal(true);
  };

  const handlePaymentPress = payment => {
    setSelectedPayment(payment);
    setShowPaymentModal(true);
  };

  const handlePendingPaymentPress = payment => {
    setSelectedPendingPayment(payment);
    setShowApprovalModal(true);
  };

  const handleApprovePayment = async () => {
    setIsProcessing(true);
    try {
      await api.post(`${API_ENDPOINTS.contractPayments}/${selectedPendingPayment.id}/approve`);
      Alert.alert('Payment Approved', `Payment approved.`);
      setShowApprovalModal(false);
      fetchPendingPayments();
      fetchPaymentHistory();
    } catch (error) {
      Alert.alert('Error', 'Failed to approve payment.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRejectPayment = () => {
    setShowApprovalModal(false);
    Alert.alert(
      'Reject Payment',
      `Are you sure?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Reject',
          style: 'destructive',
          onPress: async () => {
            try {
              await api.post(`${API_ENDPOINTS.contractPayments}/${selectedPendingPayment.id}/reject`);
              Alert.alert('Payment Rejected');
              fetchPendingPayments();
            } catch (error) {
              Alert.alert('Error', 'Failed to reject payment.');
            }
          },
        },
      ]
    );
  };

  const handleUpdateSettings = () => {
    Alert.alert('Settings Updated', 'Your settings have been saved.');
  };

  // Render Contracts Tab
  const renderContractsContent = () => (
    <>
      <View style={styles.walletSummary}>
        <View style={styles.walletInfo}>
          <Text style={styles.walletLabel}>Wallet Balance:</Text>
          <Text style={styles.walletBalance}>
            {formatCurrency(walletBalance)}
          </Text>
        </View>
        <TouchableOpacity
          style={styles.walletButton}
          onPress={() => setShowWalletModal(true)}
        >
          <Text style={styles.walletButtonText}>Manage Wallet</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.searchContainer}>
        <Ionicons name="search" size={20} color="#64748B" style={styles.searchIcon} />
        <TextInput
          style={styles.searchInput}
          placeholder="Search contracts..."
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
        {searchQuery ? (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <Ionicons name="close-circle" size={20} color="#64748B" />
          </TouchableOpacity>
        ) : null}
      </View>

      {loading ? (
        <ActivityIndicator size="large" color="#0e4e96" style={{ margin: 20 }} />
      ) : (
        getFilteredContracts().map(contract => (
          <View key={contract.id} style={styles.contractCard}>
            <TouchableOpacity onPress={() => handleContractPress(contract)}>
              <View style={styles.contractHeader}>
                <View>
                  <Text style={styles.contractTitle}>{contract.projectName || contract.contract_name}</Text>
                  <Text style={styles.contractSubtitle}>
                    {contract.contractType || contract.contract_type}
                  </Text>
                </View>
                <View
                  style={[
                    styles.statusBadge,
                    { backgroundColor: getStatusColor(contract.status) },
                  ]}
                >
                  <Text style={styles.statusText}>{contract.status}</Text>
                </View>
              </View>

              <View style={styles.contractDetails}>
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Subcontractor:</Text>
                  <Text style={styles.detailValue}>
                    {contract.subcontractorName || contract.second_party_name}
                  </Text>
                </View>
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Total Amount:</Text>
                  <Text style={styles.detailValue}>
                    {formatCurrency(contract.totalAmount || contract.contract_value)}
                  </Text>
                </View>
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Timeline:</Text>
                  <Text style={styles.detailValue}>
                    {contract.startDate || contract.effective_date} to {contract.endDate || contract.metadata?.end_date}
                  </Text>
                </View>
              </View>

              {contract.milestones && (
                <View style={styles.milestonesSummary}>
                  <Text style={styles.milestonesTitle}>Milestones:</Text>
                  <View style={styles.milestoneStats}>
                    <View style={styles.milestoneStat}>
                      <Text style={styles.milestoneStatValue}>
                        {contract.milestones.filter(m => m.status === 'Completed').length}
                      </Text>
                      <Text style={styles.milestoneStatLabel}>Completed</Text>
                    </View>
                    <View style={styles.milestoneStat}>
                      <Text style={styles.milestoneStatValue}>
                        {contract.milestones.filter(m => m.status === 'In Progress').length}
                      </Text>
                      <Text style={styles.milestoneStatLabel}>In Progress</Text>
                    </View>
                    <View style={styles.milestoneStat}>
                      <Text style={styles.milestoneStatValue}>
                        {contract.milestones.filter(m => m.status === 'Not Started' || m.status === 'On Hold').length}
                      </Text>
                      <Text style={styles.milestoneStatLabel}>Remaining</Text>
                    </View>
                  </View>
                </View>
              )}

              <View style={styles.contractFooter}>
                <View style={styles.progressContainer}>
                  <View style={styles.progressBar}>
                    <View
                      style={[
                        styles.progressFill,
                        {
                          width: `${contract.milestones ? (contract.milestones.filter(m => m.status === 'Completed').length / contract.milestones.length) * 100 : 0}%`,
                        },
                      ]}
                    />
                  </View>
                  <Text style={styles.progressText}>
                    {contract.milestones
                      ? Math.round(
                          (contract.milestones.filter(m => m.status === 'Completed').length /
                            contract.milestones.length) *
                            100
                        )
                      : 0}
                    % Complete
                  </Text>
                </View>
                <TouchableOpacity style={styles.viewDetailsButton}>
                  <Text style={styles.viewDetailsText}>View Details</Text>
                  <Ionicons name="chevron-forward" size={16} color="#0e4e96" />
                </TouchableOpacity>
              </View>
            </TouchableOpacity>

            {/* Export buttons */}
            <View style={styles.exportActions}>
              <TouchableOpacity
                style={styles.exportButton}
                onPress={() => handlePrintContract(contract)}
              >
                <Ionicons name="print-outline" size={20} color="#0e4e96" />
                <Text style={styles.exportButtonText}>Print</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.exportButton}
                onPress={() => handleExportPDF(contract)}
              >
                <MaterialCommunityIcons name="file-pdf-box" size={20} color="#0e4e96" />
                <Text style={styles.exportButtonText}>PDF</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.exportButton}
                onPress={() => handleExportWord(contract)}
              >
                <MaterialCommunityIcons name="file-word" size={20} color="#0e4e96" />
                <Text style={styles.exportButtonText}>Word</Text>
              </TouchableOpacity>
            </View>
          </View>
        ))
      )}

      {!loading && getFilteredContracts().length === 0 && (
        <View style={styles.emptyState}>
          <MaterialCommunityIcons name="file-document-outline" size={48} color="#CBD5E1" />
          <Text style={styles.emptyStateText}>No contracts found</Text>
          <Text style={styles.emptyStateSubtext}>Try adjusting your search criteria</Text>
        </View>
      )}
      <TouchableOpacity
        style={styles.addButton}
        onPress={() => navigation.navigate('CreateNewContractScreen')}
      >
        <Ionicons name="add" size={24} color="#fff" />
        <Text style={styles.addButtonText}>Create New Contract</Text>
      </TouchableOpacity>
    </>
  );

  // Payments Tab
  const renderPaymentsContent = () => (
    <>
      <View style={styles.searchContainer}>
        <Ionicons name="search" size={20} color="#64748B" style={styles.searchIcon} />
        <TextInput
          style={styles.searchInput}
          placeholder="Search payments..."
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
        {searchQuery ? (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <Ionicons name="close-circle" size={20} color="#64748B" />
          </TouchableOpacity>
        ) : null}
      </View>

      <View style={styles.paymentSummary}>
        <View style={styles.paymentStat}>
          <Text style={styles.paymentStatValue}>{paymentHistory.length}</Text>
          <Text style={styles.paymentStatLabel}>Total Payments</Text>
        </View>
        <View style={styles.paymentStat}>
          <Text style={styles.paymentStatValue}>
            {formatCurrency(paymentHistory.reduce((sum, p) => sum + p.amount, 0))}
          </Text>
          <Text style={styles.paymentStatLabel}>Total Amount</Text>
        </View>
      </View>

      {loading ? (
        <ActivityIndicator size="large" color="#0e4e96" style={{ margin: 20 }} />
      ) : (
        getFilteredPayments().map(payment => (
          <TouchableOpacity
            key={payment.id}
            style={styles.paymentCard}
            onPress={() => handlePaymentPress(payment)}
          >
            <View style={styles.paymentHeader}>
              <View>
                <Text style={styles.paymentTitle}>{payment.subcontractorName}</Text>
                <Text style={styles.paymentSubtitle}>{payment.projectName}</Text>
              </View>
              <Text style={styles.paymentAmount}>{formatCurrency(payment.amount)}</Text>
            </View>

            <View style={styles.paymentDetails}>
              <View style={styles.paymentDetailRow}>
                <MaterialCommunityIcons name="calendar" size={16} color="#64748B" />
                <Text style={styles.paymentDetailText}>Paid on {payment.date}</Text>
              </View>
              <View style={styles.paymentDetailRow}>
                <MaterialCommunityIcons name="check-circle-outline" size={16} color="#10B981" />
                <Text style={styles.paymentDetailText}>Status: {payment.status}</Text>
              </View>
            </View>

            <View style={styles.transactionInfo}>
              <Text style={styles.transactionLabel}>Transaction:</Text>
              <Text style={styles.transactionHash}>{payment.transactionHash}</Text>
            </View>

            <TouchableOpacity style={styles.viewDetailsButton}>
              <Text style={styles.viewDetailsText}>View Transaction</Text>
              <Ionicons name="chevron-forward" size={16} color="#0e4e96" />
            </TouchableOpacity>
          </TouchableOpacity>
        ))
      )}

      {!loading && getFilteredPayments().length === 0 && (
        <View style={styles.emptyState}>
          <MaterialCommunityIcons name="cash-multiple" size={48} color="#CBD5E1" />
          <Text style={styles.emptyStateText}>No payments found</Text>
          <Text style={styles.emptyStateSubtext}>Try adjusting your search criteria</Text>
        </View>
      )}
    </>
  );

  // Pending Payments Tab
  const renderPendingContent = () => (
    <>
      <View style={styles.searchContainer}>
        <Ionicons name="search" size={20} color="#64748B" style={styles.searchIcon} />
        <TextInput
          style={styles.searchInput}
          placeholder="Search pending payments..."
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
        {searchQuery ? (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <Ionicons name="close-circle" size={20} color="#64748B" />
          </TouchableOpacity>
        ) : null}
      </View>

      <View style={styles.pendingSummary}>
        <View style={styles.pendingStat}>
          <Text style={styles.pendingStatValue}>{pendingPayments.length}</Text>
          <Text style={styles.pendingStatLabel}>Pending Payments</Text>
        </View>
        <View style={styles.pendingStat}>
          <Text style={styles.pendingStatValue}>
            {formatCurrency(pendingPayments.reduce((sum, p) => sum + p.amount, 0))}
          </Text>
          <Text style={styles.pendingStatLabel}>Total Amount</Text>
        </View>
      </View>

      {loading ? (
        <ActivityIndicator size="large" color="#0e4e96" style={{ margin: 20 }} />
      ) : (
        getFilteredPendingPayments().map(payment => (
          <TouchableOpacity
            key={payment.id}
            style={styles.pendingCard}
            onPress={() => handlePendingPaymentPress(payment)}
          >
            <View style={styles.pendingHeader}>
              <View>
                <Text style={styles.pendingTitle}>{payment.subcontractorName}</Text>
                <Text style={styles.pendingSubtitle}>{payment.projectName}</Text>
              </View>
              <View
                style={[
                  styles.statusBadge,
                  { backgroundColor: getStatusColor(payment.status) },
                ]}
              >
                <Text style={styles.statusText}>{payment.status}</Text>
              </View>
            </View>

            <View style={styles.pendingDetails}>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Milestone:</Text>
                <Text style={styles.detailValue}>{payment.milestoneTitle}</Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Amount:</Text>
                <Text style={styles.detailValue}>{formatCurrency(payment.amount)}</Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Due Date:</Text>
                <Text style={styles.detailValue}>{payment.dueDate}</Text>
              </View>
            </View>

            {payment.completionPercentage !== undefined && (
              <View style={styles.progressContainer}>
                <View style={styles.progressBar}>
                  <View style={[styles.progressFill, { width: `${payment.completionPercentage}%` }]} />
                </View>
                <Text style={styles.progressText}>{payment.completionPercentage}% Complete</Text>
              </View>
            )}

            <View style={styles.pendingActions}>
              {payment.status === 'Pending Approval' && (
                <TouchableOpacity
                  style={[styles.pendingActionButton, styles.approveButton]}
                  onPress={() => handlePendingPaymentPress(payment)}
                >
                  <Text style={styles.pendingActionButtonText}>Review & Approve</Text>
                </TouchableOpacity>
              )}

              {payment.status === 'Processing' && (
                <View style={styles.processingInfo}>
                  <MaterialCommunityIcons name="progress-clock" size={20} color="#FBBF24" />
                  <Text style={styles.processingText}>
                    Processing - Est. completion: {payment.estimatedCompletionDate}
                  </Text>
                </View>
              )}

              {payment.status === 'Not Submitted' && (
                <View style={styles.notSubmittedInfo}>
                  <MaterialCommunityIcons name="clock-outline" size={20} color="#64748B" />
                  <Text style={styles.notSubmittedText}>Waiting for milestone completion</Text>
                </View>
              )}
            </View>
          </TouchableOpacity>
        ))
      )}

      {!loading && getFilteredPendingPayments().length === 0 && (
        <View style={styles.emptyState}>
          <MaterialCommunityIcons name="clock-outline" size={48} color="#CBD5E1" />
          <Text style={styles.emptyStateText}>No pending payments</Text>
          <Text style={styles.emptyStateSubtext}>All payments are up to date</Text>
        </View>
      )}
    </>
  );

  // Settings Tab
  // ═══════════════════════════════════════════
  // ANALYTICS TAB
  // ═══════════════════════════════════════════
  const renderAnalyticsContent = () => {
    const totalContractValue = contracts.reduce((s, c) => s + (parseFloat(c.totalAmount || c.contract_value || 0)), 0);
    const totalPaid = paymentHistory.reduce((s, p) => s + (parseFloat(p.amount || 0)), 0);
    const totalPending = pendingPayments.reduce((s, p) => s + (parseFloat(p.amount || 0)), 0);
    const activeContracts = contracts.filter(c => c.status === 'active' || c.status === 'in_progress').length;
    const completedContracts = contracts.filter(c => c.status === 'completed').length;
    const avgContractValue = contracts.length > 0 ? totalContractValue / contracts.length : 0;
    const collectionRate = totalContractValue > 0 ? (totalPaid / totalContractValue * 100) : 0;

    // By type breakdown
    const byType = {};
    contracts.forEach(c => {
      const t = c.contractType || c.contract_type || 'Other';
      byType[t] = (byType[t] || 0) + parseFloat(c.totalAmount || c.contract_value || 0);
    });

    // By status
    const byStatus = {};
    contracts.forEach(c => { byStatus[c.status] = (byStatus[c.status] || 0) + 1; });

    // Monthly revenue
    const monthlyRev = {};
    paymentHistory.forEach(p => {
      const d = new Date(p.date || p.created_at);
      const key = d.toLocaleDateString('en-GB', { month: 'short' });
      monthlyRev[key] = (monthlyRev[key] || 0) + parseFloat(p.amount || 0);
    });

    return (
      <View style={{padding:16}}>
        {/* KPI Cards */}
        <View style={{flexDirection:'row',flexWrap:'wrap',gap:8,marginBottom:16}}>
          {[{l:'Total Value',v:formatCurrency(totalContractValue),c:'#0e4e96',i:'file-document'},
            {l:'Collected',v:formatCurrency(totalPaid),c:'#10B981',i:'check-circle'},
            {l:'Pending',v:formatCurrency(totalPending),c:'#F59E0B',i:'clock-outline'},
            {l:'Outstanding',v:formatCurrency(totalContractValue - totalPaid - totalPending),c:'#EF4444',i:'alert-circle'},
          ].map((k,i) => (
            <View key={i} style={{width:'48%',backgroundColor:'#fff',borderRadius:14,padding:14,borderWidth:1,borderColor:'#E2E8F0'}}>
              <View style={{flexDirection:'row',alignItems:'center',gap:6,marginBottom:6}}>
                <MaterialCommunityIcons name={k.i} size={16} color={k.c} />
                <Text style={{fontSize:11,color:'#64748B',fontWeight:'600'}}>{k.l}</Text>
              </View>
              <Text style={{fontSize:18,fontWeight:'900',color:k.c}}>{k.v}</Text>
            </View>
          ))}
        </View>

        {/* Contract Stats */}
        <View style={{backgroundColor:'#fff',borderRadius:14,padding:16,marginBottom:12,borderWidth:1,borderColor:'#E2E8F0'}}>
          <Text style={{fontSize:15,fontWeight:'800',color:'#0F172A',marginBottom:12}}>Contract Statistics</Text>
          {[{l:'Total Contracts',v:String(contracts.length),c:'#0e4e96'},
            {l:'Active',v:String(activeContracts),c:'#10B981'},
            {l:'Completed',v:String(completedContracts),c:'#64748B'},
            {l:'Average Value',v:formatCurrency(avgContractValue),c:'#8B5CF6'},
            {l:'Collection Rate',v:collectionRate.toFixed(1)+'%',c:collectionRate>80?'#10B981':collectionRate>50?'#F59E0B':'#EF4444'},
            {l:'Total Payments',v:String(paymentHistory.length),c:'#0891B2'},
          ].map((r,i) => (
            <View key={i} style={{flexDirection:'row',justifyContent:'space-between',paddingVertical:8,borderBottomWidth:i<5?1:0,borderBottomColor:'#F1F5F9'}}>
              <Text style={{fontSize:13,color:'#475569'}}>{r.l}</Text>
              <Text style={{fontSize:14,fontWeight:'700',color:r.c}}>{r.v}</Text>
            </View>
          ))}
        </View>

        {/* By Status */}
        <View style={{backgroundColor:'#fff',borderRadius:14,padding:16,marginBottom:12,borderWidth:1,borderColor:'#E2E8F0'}}>
          <Text style={{fontSize:15,fontWeight:'800',color:'#0F172A',marginBottom:12}}>By Status</Text>
          <View style={{flexDirection:'row',gap:8}}>
            {Object.entries(byStatus).map(([status, count]) => {
              const c = status==='active'||status==='in_progress'?'#10B981':status==='completed'?'#64748B':status==='draft'?'#F59E0B':'#EF4444';
              return (
                <View key={status} style={{flex:1,backgroundColor:c+'10',borderRadius:10,padding:10,alignItems:'center'}}>
                  <Text style={{fontSize:18,fontWeight:'900',color:c}}>{count}</Text>
                  <Text style={{fontSize:9,color:'#64748B',textTransform:'capitalize'}}>{status}</Text>
                </View>
              );
            })}
          </View>
        </View>

        {/* By Type */}
        {Object.keys(byType).length > 0 && (
          <View style={{backgroundColor:'#fff',borderRadius:14,padding:16,marginBottom:12,borderWidth:1,borderColor:'#E2E8F0'}}>
            <Text style={{fontSize:15,fontWeight:'800',color:'#0F172A',marginBottom:12}}>By Contract Type</Text>
            {Object.entries(byType).sort((a,b) => b[1]-a[1]).map(([type, value], i) => {
              const maxVal = Math.max(...Object.values(byType));
              const pct = maxVal > 0 ? (value / maxVal * 100) : 0;
              return (
                <View key={type} style={{marginBottom:10}}>
                  <View style={{flexDirection:'row',justifyContent:'space-between',marginBottom:4}}>
                    <Text style={{fontSize:12,color:'#334155',fontWeight:'600',textTransform:'capitalize'}}>{type}</Text>
                    <Text style={{fontSize:12,fontWeight:'700',color:'#0e4e96'}}>{formatCurrency(value)}</Text>
                  </View>
                  <View style={{height:8,backgroundColor:'#E2E8F0',borderRadius:4,overflow:'hidden'}}>
                    <View style={{width:pct+'%',height:'100%',backgroundColor:'#0e4e96',borderRadius:4}} />
                  </View>
                </View>
              );
            })}
          </View>
        )}

        {/* Payment Timeline */}
        {Object.keys(monthlyRev).length > 0 && (
          <View style={{backgroundColor:'#fff',borderRadius:14,padding:16,marginBottom:12,borderWidth:1,borderColor:'#E2E8F0'}}>
            <Text style={{fontSize:15,fontWeight:'800',color:'#0F172A',marginBottom:12}}>Payment Timeline</Text>
            <View style={{flexDirection:'row',alignItems:'flex-end',height:80,gap:4}}>
              {Object.entries(monthlyRev).map(([month, amount]) => {
                const maxAmt = Math.max(...Object.values(monthlyRev), 1);
                return (
                  <View key={month} style={{flex:1,alignItems:'center'}}>
                    <View style={{width:'70%',height:Math.max(4,(amount/maxAmt)*70),backgroundColor:'#10B981',borderRadius:3}} />
                    <Text style={{fontSize:8,color:'#94A3B8',marginTop:3}}>{month}</Text>
                  </View>
                );
              })}
            </View>
          </View>
        )}

        {/* Cash Flow Summary */}
        <View style={{backgroundColor:'#fff',borderRadius:14,padding:16,marginBottom:12,borderWidth:1,borderColor:'#E2E8F0'}}>
          <Text style={{fontSize:15,fontWeight:'800',color:'#0F172A',marginBottom:12}}>Cash Flow</Text>
          <View style={{flexDirection:'row',gap:8}}>
            <View style={{flex:1,backgroundColor:'#10B981'+'10',borderRadius:10,padding:12,alignItems:'center'}}>
              <MaterialCommunityIcons name="arrow-down-bold-circle" size={20} color="#10B981" />
              <Text style={{fontSize:14,fontWeight:'900',color:'#10B981',marginTop:4}}>{formatCurrency(totalPaid)}</Text>
              <Text style={{fontSize:9,color:'#64748B'}}>Received</Text>
            </View>
            <View style={{flex:1,backgroundColor:'#F59E0B'+'10',borderRadius:10,padding:12,alignItems:'center'}}>
              <MaterialCommunityIcons name="clock-outline" size={20} color="#F59E0B" />
              <Text style={{fontSize:14,fontWeight:'900',color:'#F59E0B',marginTop:4}}>{formatCurrency(totalPending)}</Text>
              <Text style={{fontSize:9,color:'#64748B'}}>Pending</Text>
            </View>
            <View style={{flex:1,backgroundColor:'#EF4444'+'10',borderRadius:10,padding:12,alignItems:'center'}}>
              <MaterialCommunityIcons name="alert-circle" size={20} color="#EF4444" />
              <Text style={{fontSize:14,fontWeight:'900',color:'#EF4444',marginTop:4}}>{formatCurrency(totalContractValue - totalPaid - totalPending)}</Text>
              <Text style={{fontSize:9,color:'#64748B'}}>Outstanding</Text>
            </View>
          </View>
        </View>

        {/* ROI */}
        <View style={{backgroundColor:'#fff',borderRadius:14,padding:16,marginBottom:12,borderWidth:1,borderColor:'#E2E8F0'}}>
          <Text style={{fontSize:15,fontWeight:'800',color:'#0F172A',marginBottom:12}}>Financial Ratios</Text>
          {[{l:'Collection Rate',v:(collectionRate).toFixed(1)+'%',c:collectionRate>80?'#10B981':'#F59E0B'},
            {l:'Avg Payment Size',v:formatCurrency(paymentHistory.length>0?totalPaid/paymentHistory.length:0),c:'#0e4e96'},
            {l:'Pending Ratio',v:(totalContractValue>0?(totalPending/totalContractValue*100):0).toFixed(1)+'%',c:'#F59E0B'},
            {l:'Completion Rate',v:(contracts.length>0?(completedContracts/contracts.length*100):0).toFixed(1)+'%',c:'#8B5CF6'},
          ].map((r,i) => (
            <View key={i} style={{flexDirection:'row',justifyContent:'space-between',paddingVertical:8,borderBottomWidth:i<3?1:0,borderBottomColor:'#F1F5F9'}}>
              <Text style={{fontSize:13,color:'#475569'}}>{r.l}</Text>
              <Text style={{fontSize:14,fontWeight:'700',color:r.c}}>{r.v}</Text>
            </View>
          ))}
        </View>
      </View>
    );
  };

  const renderSettingsContent = () => (
    <>
      <View style={styles.settingsSection}>
        <Text style={styles.settingsSectionTitle}>Payment Thresholds</Text>
        <View style={styles.settingItem}>
          <View style={styles.settingLabelContainer}>
            <Text style={styles.settingLabel}>Auto-approve payments below</Text>
            <TouchableOpacity>
              <Ionicons name="information-circle-outline" size={16} color="#64748B" />
            </TouchableOpacity>
          </View>
          <View style={styles.settingValueContainer}>
            <TextInput
              style={styles.settingInput}
              value={settings.autoApproveThreshold.toString()}
              onChangeText={text =>
                setSettings({ ...settings, autoApproveThreshold: parseInt(text) || 0 })
              }
              keyboardType="numeric"
            />
            <Text style={styles.settingUnit}>USD</Text>
          </View>
        </View>
        <View style={styles.settingItem}>
          <View style={styles.settingLabelContainer}>
            <Text style={styles.settingLabel}>Require multi-signature above</Text>
            <TouchableOpacity>
              <Ionicons name="information-circle-outline" size={16} color="#64748B" />
            </TouchableOpacity>
          </View>
          <View style={styles.settingValueContainer}>
            <TextInput
              style={styles.settingInput}
              value={settings.requireMultiSigAbove.toString()}
              onChangeText={text =>
                setSettings({ ...settings, requireMultiSigAbove: parseInt(text) || 0 })
              }
              keyboardType="numeric"
            />
            <Text style={styles.settingUnit}>USD</Text>
          </View>
        </View>
      </View>

      <View style={styles.settingsSection}>
        <Text style={styles.settingsSectionTitle}>Notifications</Text>
        <View style={styles.settingItem}>
          <View style={styles.settingLabelContainer}>
            <Text style={styles.settingLabel}>Notify on milestone approach</Text>
          </View>
          <Switch
            value={settings.notifyOnMilestoneApproach}
            onValueChange={value =>
              setSettings({ ...settings, notifyOnMilestoneApproach: value })
            }
            trackColor={{ false: '#E2E8F0', true: '#0e4e96' }}
            thumbColor="#fff"
          />
        </View>
      </View>

      <View style={styles.settingsSection}>
        <Text style={styles.settingsSectionTitle}>Transaction Settings</Text>
        <View style={styles.settingItem}>
          <View style={styles.settingLabelContainer}>
            <Text style={styles.settingLabel}>Default gas price</Text>
          </View>
          <View style={styles.segmentedControl}>
            {['Slow', 'Standard', 'Fast'].map(option => (
              <TouchableOpacity
                key={option}
                style={[
                  styles.segmentedControlOption,
                  settings.defaultGasPrice === option && styles.segmentedControlOptionActive,
                ]}
                onPress={() => setSettings({ ...settings, defaultGasPrice: option })}
              >
                <Text
                  style={[
                    styles.segmentedControlText,
                    settings.defaultGasPrice === option && styles.segmentedControlTextActive,
                  ]}
                >
                  {option}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
        <View style={styles.settingItem}>
          <View style={styles.settingLabelContainer}>
            <Text style={styles.settingLabel}>Payment currency</Text>
          </View>
          <View style={styles.currencySelector}>
            <TouchableOpacity
              style={[
                styles.currencyOption,
                settings.paymentCurrency === 'ETH' && styles.currencyOptionActive,
              ]}
              onPress={() => setSettings({ ...settings, paymentCurrency: 'ETH' })}
            >
              <MaterialCommunityIcons name="ethereum" size={20} color={settings.paymentCurrency === 'ETH' ? '#0e4e96' : '#64748B'} />
              <Text style={[styles.currencyText, settings.paymentCurrency === 'ETH' && styles.currencyTextActive]}>ETH</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.currencyOption,
                settings.paymentCurrency === 'USDC' && styles.currencyOptionActive,
              ]}
              onPress={() => setSettings({ ...settings, paymentCurrency: 'USDC' })}
            >
              <MaterialCommunityIcons name="currency-usd-circle" size={20} color={settings.paymentCurrency === 'USDC' ? '#0e4e96' : '#64748B'} />
              <Text style={[styles.currencyText, settings.paymentCurrency === 'USDC' && styles.currencyTextActive]}>USDC</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>

      <View style={styles.settingsSection}>
        <Text style={styles.settingsSectionTitle}>Wallet Information</Text>
        <View style={styles.walletInfoItem}>
          <Text style={styles.walletInfoLabel}>Connected Wallet:</Text>
          <View style={styles.walletAddressContainer}>
            <Text style={styles.walletAddress}>{walletAddress}</Text>
            <TouchableOpacity>
              <Ionicons name="copy-outline" size={20} color="#0e4e96" />
            </TouchableOpacity>
          </View>
        </View>
        <View style={styles.walletInfoItem}>
          <Text style={styles.walletInfoLabel}>Current Balance:</Text>
          <Text style={styles.walletInfoValue}>{formatCurrency(walletBalance)}</Text>
        </View>
        <View style={styles.walletInfoItem}>
          <Text style={styles.walletInfoLabel}>Network Status:</Text>
          <View style={styles.networkStatusContainer}>
            <View style={[styles.networkIndicator, { backgroundColor: '#10B981' }]} />
            <Text style={styles.networkStatus}>Connected (Ethereum Mainnet)</Text>
          </View>
        </View>
        <View style={styles.walletInfoItem}>
          <Text style={styles.walletInfoLabel}>Gas Price:</Text>
          <Text style={styles.walletInfoValue}>
            {networkGasPrice} ETH (Congestion: {networkCongestion})
          </Text>
        </View>
      </View>

      <TouchableOpacity style={styles.saveSettingsButton} onPress={handleUpdateSettings}>
        <Text style={styles.saveSettingsButtonText}>Save Settings</Text>
      </TouchableOpacity>
    </>
  );

  // Contract Detail Modal
  const renderContractModal = () =>
    selectedContract && (
      <Modal
        visible={showContractModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowContractModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>{selectedContract.projectName || selectedContract.contract_name}</Text>
              <TouchableOpacity onPress={() => setShowContractModal(false)}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>

            <SectionList
              sections={[
                { title: 'details', data: ['details'] },
                { title: 'milestones', data: selectedContract.milestones || [] },
              ]}
              keyExtractor={(item, index) => (typeof item === 'string' ? item : item.id)}
              renderSectionHeader={({ section }) => {
                if (section.title === 'milestones') {
                  return <Text style={styles.sectionHeading}>Milestones</Text>;
                }
                return null;
              }}
              renderItem={({ item, section }) => {
                if (section.title === 'details') {
                  return (
                    <View style={styles.contractModalDetails}>
                      <View style={styles.contractTypeContainer}>
                        <Text style={styles.contractType}>{selectedContract.contractType || selectedContract.contract_type}</Text>
                        <View
                          style={[
                            styles.statusBadge,
                            { backgroundColor: getStatusColor(selectedContract.status) },
                          ]}
                        >
                          <Text style={styles.statusText}>{selectedContract.status}</Text>
                        </View>
                      </View>

                      <View style={styles.contractInfoGrid}>
                        <View style={styles.contractInfoItem}>
                          <Text style={styles.contractInfoLabel}>Subcontractor</Text>
                          <Text style={styles.contractInfoValue}>
                            {selectedContract.subcontractorName || selectedContract.second_party_name}
                          </Text>
                        </View>
                        <View style={styles.contractInfoItem}>
                          <Text style={styles.contractInfoLabel}>Total Amount</Text>
                          <Text style={styles.contractInfoValue}>
                            {formatCurrency(selectedContract.totalAmount || selectedContract.contract_value)}
                          </Text>
                        </View>
                        <View style={styles.contractInfoItem}>
                          <Text style={styles.contractInfoLabel}>Start Date</Text>
                          <Text style={styles.contractInfoValue}>
                            {selectedContract.startDate || selectedContract.effective_date}
                          </Text>
                        </View>
                        <View style={styles.contractInfoItem}>
                          <Text style={styles.contractInfoLabel}>End Date</Text>
                          <Text style={styles.contractInfoValue}>
                            {selectedContract.endDate || selectedContract.metadata?.end_date}
                          </Text>
                        </View>
                      </View>

                      {selectedContract.milestones && (
                        <View style={styles.contractProgress}>
                          <Text style={styles.contractProgressLabel}>Contract Progress</Text>
                          <View style={styles.progressBar}>
                            <View
                              style={[
                                styles.progressFill,
                                {
                                  width: `${(selectedContract.milestones.filter(m => m.status === 'Completed').length / selectedContract.milestones.length) * 100}%`,
                                },
                              ]}
                            />
                          </View>
                          <Text style={styles.progressText}>
                            {Math.round(
                              (selectedContract.milestones.filter(m => m.status === 'Completed').length /
                                selectedContract.milestones.length) *
                                100
                            )}
                            % Complete
                          </Text>
                        </View>
                      )}
                    </View>
                  );
                } else {
                  const milestone = item;
                  return (
                    <View style={styles.milestoneItem}>
                      <View style={styles.milestoneHeader}>
                        <Text style={styles.milestoneTitle}>{milestone.title}</Text>
                        <View
                          style={[
                            styles.statusBadge,
                            { backgroundColor: getStatusColor(milestone.status) },
                          ]}
                        >
                          <Text style={styles.statusText}>{milestone.status}</Text>
                        </View>
                      </View>

                      <View style={styles.milestoneDetails}>
                        <View style={styles.milestoneDetailRow}>
                          <Text style={styles.milestoneDetailLabel}>Amount:</Text>
                          <Text style={styles.milestoneDetailValue}>{formatCurrency(milestone.amount)}</Text>
                        </View>
                        <View style={styles.milestoneDetailRow}>
                          <Text style={styles.milestoneDetailLabel}>Due Date:</Text>
                          <Text style={styles.milestoneDetailValue}>{milestone.dueDate}</Text>
                        </View>
                        <View style={styles.milestoneDetailRow}>
                          <Text style={styles.milestoneDetailLabel}>Payment Status:</Text>
                          <View
                            style={[
                              styles.paymentStatusBadge,
                              { backgroundColor: getStatusColor(milestone.paymentStatus) },
                            ]}
                          >
                            <Text style={styles.paymentStatusText}>{milestone.paymentStatus}</Text>
                          </View>
                        </View>
                        {milestone.paymentDate && (
                          <View style={styles.milestoneDetailRow}>
                            <Text style={styles.milestoneDetailLabel}>Payment Date:</Text>
                            <Text style={styles.milestoneDetailValue}>{milestone.paymentDate}</Text>
                          </View>
                        )}
                        {milestone.transactionHash && (
                          <View style={styles.milestoneDetailRow}>
                            <Text style={styles.milestoneDetailLabel}>Transaction:</Text>
                            <Text style={styles.transactionHashSmall}>{milestone.transactionHash}</Text>
                          </View>
                        )}
                        {milestone.completionPercentage !== undefined && (
                          <View style={styles.milestoneProgress}>
                            <Text style={styles.milestoneProgressLabel}>Progress:</Text>
                            <View style={styles.progressBarSmall}>
                              <View style={[styles.progressFill, { width: `${milestone.completionPercentage}%` }]} />
                            </View>
                            <Text style={styles.progressTextSmall}>{milestone.completionPercentage}%</Text>
                          </View>
                        )}
                        {milestone.holdReason && (
                          <View style={styles.holdReasonContainer}>
                            <Text style={styles.holdReasonLabel}>Hold Reason:</Text>
                            <Text style={styles.holdReasonText}>{milestone.holdReason}</Text>
                          </View>
                        )}
                      </View>

                      {milestone.status === 'In Progress' && milestone.paymentStatus === 'Pending' && (
                        <TouchableOpacity
                          style={styles.reviewMilestoneButton}
                          onPress={() => {
                            setShowContractModal(false);
                            const pending = pendingPayments.find(p => p.milestoneId === milestone.id);
                            if (pending) {
                              setSelectedPendingPayment(pending);
                              setShowApprovalModal(true);
                            } else {
                              Alert.alert('Info', 'No pending payment request found for this milestone.');
                            }
                          }}
                        >
                          <Text style={styles.reviewMilestoneButtonText}>Review Payment Request</Text>
                        </TouchableOpacity>
                      )}

                      {milestone.completionEvidence && (
                        <TouchableOpacity style={styles.viewEvidenceButton}>
                          <MaterialCommunityIcons name="file-document-outline" size={16} color="#0e4e96" />
                          <Text style={styles.viewEvidenceText}>View Completion Evidence</Text>
                        </TouchableOpacity>
                      )}
                    </View>
                  );
                }
              }}
              ListFooterComponent={
                <View style={styles.modalFooter}>
                  <TouchableOpacity
                    style={styles.exportContractButton}
                    onPress={() => {
                      setShowContractModal(false);
                      handleExportPDF(selectedContract);
                    }}
                  >
                    <MaterialCommunityIcons name="file-export-outline" size={20} color="#0e4e96" />
                    <Text style={styles.exportContractText}>Export Contract</Text>
                  </TouchableOpacity>
                </View>
              }
            />
          </View>
        </View>
      </Modal>
    );

  // Payment Detail Modal
  const renderPaymentModal = () =>
    selectedPayment && (
      <Modal
        visible={showPaymentModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowPaymentModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Payment Details</Text>
              <TouchableOpacity onPress={() => setShowPaymentModal(false)}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>

            <View style={styles.paymentModalContent}>
              <View style={styles.paymentAmountContainer}>
                <Text style={styles.paymentAmountLabel}>Amount Paid</Text>
                <Text style={styles.paymentAmountValue}>
                  {formatCurrency(selectedPayment.amount)}
                </Text>
              </View>

              <View style={styles.paymentInfoGrid}>
                <View style={styles.paymentInfoItem}>
                  <Text style={styles.paymentInfoLabel}>Recipient</Text>
                  <Text style={styles.paymentInfoValue}>
                    {selectedPayment.subcontractorName}
                  </Text>
                </View>
                <View style={styles.paymentInfoItem}>
                  <Text style={styles.paymentInfoLabel}>Project</Text>
                  <Text style={styles.paymentInfoValue}>
                    {selectedPayment.projectName}
                  </Text>
                </View>
                <View style={styles.paymentInfoItem}>
                  <Text style={styles.paymentInfoLabel}>Date</Text>
                  <Text style={styles.paymentInfoValue}>
                    {selectedPayment.date}
                  </Text>
                </View>
                <View style={styles.paymentInfoItem}>
                  <Text style={styles.paymentInfoLabel}>Status</Text>
                  <View
                    style={[
                      styles.statusBadge,
                      { backgroundColor: getStatusColor(selectedPayment.status) },
                    ]}
                  >
                    <Text style={styles.statusText}>
                      {selectedPayment.status}
                    </Text>
                  </View>
                </View>
              </View>

              <View style={styles.transactionDetails}>
                <Text style={styles.transactionDetailsTitle}>Transaction Details</Text>

                <View style={styles.transactionDetailRow}>
                  <Text style={styles.transactionDetailLabel}>Transaction Hash:</Text>
                  <View style={styles.transactionHashContainer}>
                    <Text style={styles.transactionHashDetail}>{selectedPayment.transactionHash}</Text>
                    <TouchableOpacity>
                      <Ionicons name="copy-outline" size={20} color="#0e4e96" />
                    </TouchableOpacity>
                  </View>
                </View>

                <View style={styles.transactionDetailRow}>
                  <Text style={styles.transactionDetailLabel}>Gas Used:</Text>
                  <Text style={styles.transactionDetailValue}>{selectedPayment.gasUsed} ETH</Text>
                </View>

                <View style={styles.transactionDetailRow}>
                  <Text style={styles.transactionDetailLabel}>Network Fee:</Text>
                  <Text style={styles.transactionDetailValue}>${selectedPayment.networkFee}</Text>
                </View>

                <View style={styles.transactionDetailRow}>
                  <Text style={styles.transactionDetailLabel}>Total Cost:</Text>
                  <Text style={styles.transactionDetailTotal}>
                    {formatCurrency(selectedPayment.amount + selectedPayment.networkFee)}
                  </Text>
                </View>
              </View>

              <View style={styles.paymentModalActions}>
                <TouchableOpacity
                  style={styles.viewBlockExplorerButton}
                  onPress={() => setShowPaymentModal(false)}
                >
                  <MaterialCommunityIcons name="open-in-new" size={20} color="#0e4e96" />
                  <Text style={styles.viewBlockExplorerText}>View on Block Explorer</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.downloadReceiptButton}
                  onPress={() => {
                    setShowPaymentModal(false);
                    Alert.alert('Receipt Downloaded', 'The payment receipt has been downloaded to your device.');
                  }}
                >
                  <MaterialCommunityIcons name="receipt" size={20} color="#0e4e96" />
                  <Text style={styles.downloadReceiptText}>Download Receipt</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </Modal>
    );

  // Payment Approval Modal
  const renderApprovalModal = () =>
    selectedPendingPayment && (
      <Modal
        visible={showApprovalModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowApprovalModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Payment Approval</Text>
              <TouchableOpacity onPress={() => setShowApprovalModal(false)}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>

            <View style={styles.approvalModalContent}>
              <View style={styles.approvalAmountContainer}>
                <Text style={styles.approvalAmountLabel}>Payment Amount</Text>
                <Text style={styles.approvalAmountValue}>
                  {formatCurrency(selectedPendingPayment.amount)}
                </Text>
              </View>

              <View style={styles.approvalInfoGrid}>
                <View style={styles.approvalInfoItem}>
                  <Text style={styles.approvalInfoLabel}>Recipient</Text>
                  <Text style={styles.approvalInfoValue}>
                    {selectedPendingPayment.subcontractorName}
                  </Text>
                </View>
                <View style={styles.approvalInfoItem}>
                  <Text style={styles.approvalInfoLabel}>Project</Text>
                  <Text style={styles.approvalInfoValue}>
                    {selectedPendingPayment.projectName}
                  </Text>
                </View>
                <View style={styles.approvalInfoItem}>
                  <Text style={styles.approvalInfoLabel}>Milestone</Text>
                  <Text style={styles.approvalInfoValue}>
                    {selectedPendingPayment.milestoneTitle}
                  </Text>
                </View>
                <View style={styles.approvalInfoItem}>
                  <Text style={styles.approvalInfoLabel}>Due Date</Text>
                  <Text style={styles.approvalInfoValue}>
                    {selectedPendingPayment.dueDate}
                  </Text>
                </View>
              </View>

              {selectedPendingPayment.submissionDate && (
                <View style={styles.submissionDetails}>
                  <Text style={styles.submissionDetailsTitle}>Submission Details</Text>

                  <View style={styles.submissionDetailRow}>
                    <Text style={styles.submissionDetailLabel}>Submitted On:</Text>
                    <Text style={styles.submissionDetailValue}>
                      {selectedPendingPayment.submissionDate}
                    </Text>
                  </View>

                  {selectedPendingPayment.approvalStatus && (
                    <View style={styles.submissionDetailRow}>
                      <Text style={styles.submissionDetailLabel}>Approval Status:</Text>
                      <View
                        style={[
                          styles.approvalStatusBadge,
                          {
                            backgroundColor:
                              selectedPendingPayment.approvalStatus === 'Approved'
                                ? '#10B981'
                                : selectedPendingPayment.approvalStatus === 'Under Review'
                                ? '#FBBF24'
                                : '#64748B',
                          },
                        ]}
                      >
                        <Text style={styles.approvalStatusText}>{selectedPendingPayment.approvalStatus}</Text>
                      </View>
                    </View>
                  )}

                  {selectedPendingPayment.approvalDate && (
                    <View style={styles.submissionDetailRow}>
                      <Text style={styles.submissionDetailLabel}>Approved On:</Text>
                      <Text style={styles.submissionDetailValue}>
                        {selectedPendingPayment.approvalDate}
                      </Text>
                    </View>
                  )}
                </View>
              )}

              <View style={styles.smartContractDetails}>
                <Text style={styles.smartContractDetailsTitle}>Smart Contract Details</Text>

                <View style={styles.smartContractDetailRow}>
                  <Text style={styles.smartContractDetailLabel}>Estimated Gas:</Text>
                  <Text style={styles.smartContractDetailValue}>0.0035 ETH</Text>
                </View>
                <View style={styles.smartContractDetailRow}>
                  <Text style={styles.smartContractDetailLabel}>Network Fee:</Text>
                  <Text style={styles.smartContractDetailValue}>$0.28</Text>
                </View>
                <View style={styles.smartContractDetailRow}>
                  <Text style={styles.smartContractDetailLabel}>Total Cost:</Text>
                  <Text style={styles.smartContractDetailTotal}>
                    {formatCurrency(selectedPendingPayment.amount + 0.28)}
                  </Text>
                </View>
              </View>

              <View style={styles.approvalModalActions}>
                {selectedPendingPayment.status === 'Pending Approval' && (
                  <>
                    <TouchableOpacity
                      style={styles.approvePaymentButton}
                      onPress={handleApprovePayment}
                      disabled={isProcessing}
                    >
                      {isProcessing ? (
                        <ActivityIndicator size={20} color="#fff" />
                      ) : (
                        <>
                          <MaterialCommunityIcons name="check-circle-outline" size={20} color="#fff" />
                          <Text style={styles.approvePaymentText}>Approve Payment</Text>
                        </>
                      )}
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={styles.rejectPaymentButton}
                      onPress={handleRejectPayment}
                      disabled={isProcessing}
                    >
                      <MaterialCommunityIcons name="close-circle-outline" size={20} color="#EF4444" />
                      <Text style={styles.rejectPaymentText}>Reject</Text>
                    </TouchableOpacity>
                  </>
                )}

                {selectedPendingPayment.status === 'Processing' && (
                  <View style={styles.processingStatusContainer}>
                    <ActivityIndicator size={20} color="#FBBF24" />
                    <Text style={styles.processingStatusText}>
                      Payment is being processed through the smart contract
                    </Text>
                  </View>
                )}
              </View>
            </View>
          </View>
        </View>
      </Modal>
    );

  // Wallet Modal
  const renderWalletModal = () => (
    <Modal
      visible={showWalletModal}
      transparent={true}
      animationType="slide"
      onRequestClose={() => setShowWalletModal(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Wallet Management</Text>
            <TouchableOpacity onPress={() => setShowWalletModal(false)}>
              <Ionicons name="close" size={24} color="#64748B" />
            </TouchableOpacity>
          </View>

          <View style={styles.walletModalContent}>
            <View style={styles.walletBalanceContainer}>
              <Text style={styles.walletBalanceLabel}>Current Balance</Text>
              <Text style={styles.walletBalanceValue}>{formatCurrency(walletBalance)}</Text>
            </View>

            <View style={styles.walletAddressSection}>
              <Text style={styles.walletAddressLabel}>Wallet Address</Text>
              <View style={styles.walletAddressRow}>
                <Text style={styles.walletAddressValue}>{walletAddress}</Text>
                <TouchableOpacity style={styles.copyButton}>
                  <Ionicons name="copy-outline" size={20} color="#0e4e96" />
                </TouchableOpacity>
              </View>
            </View>

            <View style={styles.walletNetworkSection}>
              <Text style={styles.walletNetworkLabel}>Network</Text>
              <View style={styles.walletNetworkRow}>
                <View style={styles.networkIndicator} />
                <Text style={styles.walletNetworkValue}>Ethereum Mainnet</Text>
              </View>
            </View>

            <View style={styles.walletActionsContainer}>
              <TouchableOpacity style={styles.walletActionButton}>
                <MaterialCommunityIcons name="bank-transfer" size={24} color="#0e4e96" />
                <Text style={styles.walletActionText}>Deposit Funds</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.walletActionButton}>
                <MaterialCommunityIcons name="cash-multiple" size={24} color="#0e4e96" />
                <Text style={styles.walletActionText}>Withdraw</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.walletActionButton}>
                <MaterialCommunityIcons name="history" size={24} color="#0e4e96" />
                <Text style={styles.walletActionText}>Transaction History</Text>
              </TouchableOpacity>
            </View>

            <View style={styles.walletSecuritySection}>
              <Text style={styles.walletSectionTitle}>Security</Text>
              <TouchableOpacity style={styles.walletSecurityOption}>
                <View style={styles.walletSecurityOptionContent}>
                  <MaterialCommunityIcons name="shield-check" size={24} color="#0e4e96" />
                  <Text style={styles.walletSecurityOptionText}>Enable 2FA</Text>
                </View>
                <Ionicons name="chevron-forward" size={20} color="#64748B" />
              </TouchableOpacity>
              <TouchableOpacity style={styles.walletSecurityOption}>
                <View style={styles.walletSecurityOptionContent}>
                  <MaterialCommunityIcons name="key-variant" size={24} color="#0e4e96" />
                  <Text style={styles.walletSecurityOptionText}>Backup Private Keys</Text>
                </View>
                <Ionicons name="chevron-forward" size={20} color="#64748B" />
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );

  // SectionList data
  const getSectionData = () => {
    switch (activeTab) {
      case 'Contracts':
        return [
          {
            title: 'contracts_section',
            data: ['contracts_content'],
            renderItem: () => renderContractsContent(),
          },
        ];
      case 'Payments':
        return [
          {
            title: 'payments_section',
            data: ['payments_content'],
            renderItem: () => renderPaymentsContent(),
          },
        ];
      case 'Pending':
        return [
          {
            title: 'pending_section',
            data: ['pending_content'],
            renderItem: () => renderPendingContent(),
          },
        ];
      case 'Settings':
        return [
          {
            title: 'settings_section',
            data: ['settings_content'],
            renderItem: () => renderSettingsContent(),
          },
        ];
      default:
        return [];
    }
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Ionicons name="chevron-back" size={24} color="#fff" />
        </TouchableOpacity>
        {logoUrl ? (
          <Image source={{ uri: (logoUrl && logoUrl.startsWith('http')) ? logoUrl : undefined }} style={styles.headerLogo} onError={() => {}} />
        ) : (
          <MaterialCommunityIcons name="file-sign" size={30} color="#fff" />
        )}
        <Text style={styles.headerTitle}>Smart Contract Payments</Text>
        <TouchableOpacity style={styles.helpButton}>
          <Ionicons name="help-circle-outline" size={24} color="#fff" />
        </TouchableOpacity>
      </View>

      {/* Tab Navigation */}
      <View style={styles.tabContainer}>
        {['Contracts', 'Payments', 'Pending', 'Analytics', 'Settings'].map(tab => (
          <TouchableOpacity
            key={tab}
            style={[styles.tabButton, activeTab === tab && styles.activeTabButton]}
            onPress={() => setActiveTab(tab)}
          >
            <Text style={[styles.tabText, activeTab === tab && styles.activeTabText]}>{tab}</Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Main Content */}
      <SectionList
        sections={getSectionData()}
        keyExtractor={(item, index) => `${item}_${index}`}
        renderItem={({ item, section }) => section.renderItem({ item })}
        renderSectionHeader={() => null}
        stickySectionHeadersEnabled={false}
        contentContainerStyle={styles.sectionListContent}
      />

      {/* Modals */}
      {renderContractModal()}
      {renderPaymentModal()}
      {renderApprovalModal()}
      {renderWalletModal()}
    </SafeAreaView>
  );
};

// ==================== STYLES ====================
const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#0F172A',
  },
  header: {
    backgroundColor: '#4A90E2',
    paddingVertical: 15,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLogo: {
    width: 30,
    height: 30,
    borderRadius: 15,
    resizeMode: 'cover',
  },
  headerTitle: {
    color: '#fff',
    fontSize: 20,
    fontWeight: 'bold',
    flex: 1,
    textAlign: 'center',
  },
  helpButton: {
    padding: 5,
  },
  tabContainer: {
    backgroundColor: '#fff',
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e1e1e1',
  },
  tabButton: {
    flex: 1,
    paddingVertical: 15,
    alignItems: 'center',
  },
  activeTabButton: {
    borderBottomWidth: 2,
    borderBottomColor: '#0e4e96',
  },
  tabText: {
    fontSize: 14,
    color: '#64748B',
  },
  activeTabText: {
    color: '#0e4e96',
    fontWeight: 'bold',
  },
  sectionListContent: {
    padding: 16,
    paddingBottom: 80,
  },

  // Wallet Summary
  walletSummary: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  walletInfo: {
    flex: 1,
  },
  walletLabel: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 4,
  },
  walletBalance: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#0e4e96',
  },
  walletButton: {
    backgroundColor: '#0e4e96',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
  },
  walletButtonText: {
    color: '#fff',
    fontWeight: '500',
  },

  // Search
  searchContainer: {
    backgroundColor: '#fff',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginBottom: 16,
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    height: 40,
    fontSize: 16,
    color: '#333',
  },

  // Contract Card
  contractCard: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  contractHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  contractTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  contractSubtitle: {
    fontSize: 14,
    color: '#64748B',
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#fff',
  },
  contractDetails: {
    marginBottom: 12,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  detailLabel: {
    fontSize: 14,
    color: '#64748B',
  },
  detailValue: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
  },
  milestonesSummary: {
    marginBottom: 12,
  },
  milestonesTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
    marginBottom: 8,
  },
  milestoneStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  milestoneStat: {
    alignItems: 'center',
  },
  milestoneStatValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0e4e96',
    marginBottom: 2,
  },
  milestoneStatLabel: {
    fontSize: 12,
    color: '#64748B',
  },
  contractFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8,
  },
  progressContainer: {
    flex: 1,
    marginRight: 16,
  },
  progressBar: {
    height: 8,
    backgroundColor: '#e1e1e1',
    borderRadius: 4,
    overflow: 'hidden',
    marginBottom: 4,
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#0e4e96',
    borderRadius: 4,
  },
  progressText: {
    fontSize: 12,
    color: '#64748B',
  },
  viewDetailsButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  viewDetailsText: {
    fontSize: 14,
    color: '#0e4e96',
    marginRight: 4,
  },

  // Export Actions
  exportActions: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#E2E8F0',
  },
  exportButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
  },
  exportButtonText: {
    fontSize: 14,
    color: '#0e4e96',
    marginLeft: 4,
  },

  // Payment Card
  paymentSummary: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  paymentStat: {
    alignItems: 'center',
  },
  paymentStatValue: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0e4e96',
    marginBottom: 4,
  },
  paymentStatLabel: {
    fontSize: 14,
    color: '#64748B',
  },
  paymentCard: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  paymentHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  paymentTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 2,
  },
  paymentSubtitle: {
    fontSize: 14,
    color: '#64748B',
  },
  paymentAmount: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0e4e96',
  },
  paymentDetails: {
    marginBottom: 12,
  },
  paymentDetailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  paymentDetailText: {
    fontSize: 14,
    color: '#64748B',
    marginLeft: 8,
  },
  transactionInfo: {
    backgroundColor: '#f1f5f9',
    borderRadius: 6,
    padding: 8,
    marginBottom: 12,
  },
  transactionLabel: {
    fontSize: 12,
    color: '#64748B',
    marginBottom: 4,
  },
  transactionHash: {
    fontSize: 14,
    color: '#333',
    fontFamily: 'monospace',
  },

  // Pending Payment Card
  pendingSummary: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  pendingStat: {
    alignItems: 'center',
  },
  pendingStatValue: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0e4e96',
    marginBottom: 4,
  },
  pendingStatLabel: {
    fontSize: 14,
    color: '#64748B',
  },
  pendingCard: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  pendingHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  pendingTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 2,
  },
  pendingSubtitle: {
    fontSize: 14,
    color: '#64748B',
  },
  pendingDetails: {
    marginBottom: 12,
  },
  pendingActions: {
    marginTop: 12,
  },
  pendingActionButton: {
    paddingVertical: 10,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  approveButton: {
    backgroundColor: '#0e4e96',
  },
  pendingActionButtonText: {
    color: '#fff',
    fontWeight: '500',
  },
  processingInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FEF3C7',
    padding: 10,
    borderRadius: 6,
  },
  processingText: {
    fontSize: 14,
    color: '#92400E',
    marginLeft: 8,
  },
  notSubmittedInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F1F5F9',
    padding: 10,
    borderRadius: 6,
  },
  notSubmittedText: {
    fontSize: 14,
    color: '#64748B',
    marginLeft: 8,
  },

  // Settings
  settingsSection: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  settingsSectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 16,
  },
  settingItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  settingLabelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  settingLabel: {
    fontSize: 14,
    color: '#333',
    marginRight: 8,
  },
  settingValueContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  settingInput: {
    backgroundColor: '#f1f5f9',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    width: 100,
    textAlign: 'right',
  },
  settingUnit: {
    fontSize: 14,
    color: '#64748B',
    marginLeft: 8,
  },
  segmentedControl: {
    flexDirection: 'row',
    backgroundColor: '#f1f5f9',
    borderRadius: 6,
    overflow: 'hidden',
  },
  segmentedControlOption: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  segmentedControlOptionActive: {
    backgroundColor: '#0e4e96',
  },
  segmentedControlText: {
    fontSize: 14,
    color: '#64748B',
  },
  segmentedControlTextActive: {
    color: '#fff',
    fontWeight: '500',
  },
  currencySelector: {
    flexDirection: 'row',
  },
  currencyOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    marginLeft: 8,
    backgroundColor: '#f1f5f9',
  },
  currencyOptionActive: {
    backgroundColor: '#e0f2ff',
  },
  currencyText: {
    fontSize: 14,
    color: '#64748B',
    marginLeft: 4,
  },
  currencyTextActive: {
    color: '#0e4e96',
    fontWeight: '500',
  },
  walletInfoItem: {
    marginBottom: 12,
  },
  walletInfoLabel: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 4,
  },
  walletAddressContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f1f5f9',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  walletAddress: {
    fontSize: 14,
    color: '#333',
    fontFamily: 'monospace',
    flex: 1,
    marginRight: 8,
  },
  walletInfoValue: {
    fontSize: 14,
    color: '#333',
    fontWeight: '500',
  },
  networkStatusContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  networkIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#10B981',
    marginRight: 8,
  },
  networkStatus: {
    fontSize: 14,
    color: '#333',
  },
  saveSettingsButton: {
    backgroundColor: '#0e4e96',
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 16,
  },
  saveSettingsButtonText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 16,
  },

  // Empty State
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
  },
  emptyStateText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#64748B',
    marginTop: 16,
  },
  emptyStateSubtext: {
    fontSize: 14,
    color: '#94A3B8',
    marginTop: 8,
  },

  // Add Button
  addButton: {
    backgroundColor: '#0e4e96',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 8,
    marginTop: 8,
  },
  addButtonText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 16,
    marginLeft: 8,
  },

  // Modal
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    width: '90%',
    maxHeight: '80%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#e1e1e1',
    padding: 16,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  sectionHeading: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 12,
    marginTop: 16,
    paddingHorizontal: 16,
  },

  // Contract Modal
  contractModalDetails: {
    padding: 16,
  },
  contractTypeContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  contractType: {
    fontSize: 16,
    color: '#64748B',
  },
  contractInfoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 16,
  },
  contractInfoItem: {
    width: '50%',
    marginBottom: 12,
  },
  contractInfoLabel: {
    fontSize: 12,
    color: '#64748B',
    marginBottom: 4,
  },
  contractInfoValue: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
  },
  contractProgress: {
    marginTop: 8,
  },
  contractProgressLabel: {
    fontSize: 14,
    color: '#333',
    marginBottom: 8,
  },
  milestoneItem: {
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
    marginHorizontal: 16,
  },
  milestoneHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  milestoneTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
  },
  milestoneDetails: {
    marginBottom: 12,
  },
  milestoneDetailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  milestoneDetailLabel: {
    fontSize: 14,
    color: '#64748B',
    width: 120,
  },
  milestoneDetailValue: {
    fontSize: 14,
    color: '#333',
  },
  paymentStatusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  paymentStatusText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#fff',
  },
  transactionHashSmall: {
    fontSize: 12,
    color: '#333',
    fontFamily: 'monospace',
  },
  milestoneProgress: {
    marginTop: 8,
  },
  milestoneProgressLabel: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 4,
  },
  progressBarSmall: {
    height: 6,
    backgroundColor: '#e1e1e1',
    borderRadius: 3,
    overflow: 'hidden',
    marginBottom: 4,
  },
  progressTextSmall: {
    fontSize: 12,
    color: '#64748B',
  },
  holdReasonContainer: {
    backgroundColor: '#e0f2ff',
    borderRadius: 6,
    padding: 8,
    marginTop: 8,
  },
  holdReasonLabel: {
    fontSize: 12,
    color: '#0e4e96',
    fontWeight: '500',
    marginBottom: 4,
  },
  holdReasonText: {
    fontSize: 14,
    color: '#333',
  },
  reviewMilestoneButton: {
    backgroundColor: '#0e4e96',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    alignItems: 'center',
    marginTop: 8,
  },
  reviewMilestoneButtonText: {
    color: '#fff',
    fontWeight: '500',
  },
  viewEvidenceButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 12,
  },
  viewEvidenceText: {
    fontSize: 14,
    color: '#0e4e96',
    marginLeft: 8,
  },
  modalFooter: {
    borderTopWidth: 1,
    borderTopColor: '#e1e1e1',
    padding: 16,
  },
  exportContractButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  exportContractText: {
    fontSize: 16,
    color: '#0e4e96',
    fontWeight: '500',
    marginLeft: 8,
  },

  // Payment Modal
  paymentModalContent: {
    padding: 16,
  },
  paymentAmountContainer: {
    alignItems: 'center',
    marginBottom: 24,
  },
  paymentAmountLabel: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 8,
  },
  paymentAmountValue: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#0e4e96',
  },
  paymentInfoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 24,
  },
  paymentInfoItem: {
    width: '50%',
    marginBottom: 16,
  },
  paymentInfoLabel: {
    fontSize: 12,
    color: '#64748B',
    marginBottom: 4,
  },
  paymentInfoValue: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
  },
  transactionDetails: {
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    padding: 16,
    marginBottom: 24,
  },
  transactionDetailsTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 16,
  },
  transactionDetailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  transactionDetailLabel: {
    fontSize: 14,
    color: '#64748B',
    width: 120,
  },
  transactionHashContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  transactionHashDetail: {
    fontSize: 14,
    color: '#333',
    fontFamily: 'monospace',
    flex: 1,
    marginRight: 8,
  },
  transactionDetailValue: {
    fontSize: 14,
    color: '#333',
  },
  transactionDetailTotal: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0e4e96',
  },
  paymentModalActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  viewBlockExplorerButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  viewBlockExplorerText: {
    fontSize: 14,
    color: '#0e4e96',
    marginLeft: 8,
  },
  downloadReceiptButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  downloadReceiptText: {
    fontSize: 14,
    color: '#0e4e96',
    marginLeft: 8,
  },

  // Approval Modal
  approvalModalContent: {
    padding: 16,
  },
  approvalAmountContainer: {
    alignItems: 'center',
    marginBottom: 24,
  },
  approvalAmountLabel: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 8,
  },
  approvalAmountValue: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#0e4e96',
  },
  approvalInfoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 24,
  },
  approvalInfoItem: {
    width: '50%',
    marginBottom: 16,
  },
  approvalInfoLabel: {
    fontSize: 12,
    color: '#64748B',
    marginBottom: 4,
  },
  approvalInfoValue: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
  },
  submissionDetails: {
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    padding: 16,
    marginBottom: 24,
  },
  submissionDetailsTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 16,
  },
  submissionDetailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  submissionDetailLabel: {
    fontSize: 14,
    color: '#64748B',
    width: 120,
  },
  submissionDetailValue: {
    fontSize: 14,
    color: '#333',
  },
  approvalStatusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  approvalStatusText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#fff',
  },
  smartContractDetails: {
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    padding: 16,
    marginBottom: 24,
  },
  smartContractDetailsTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 16,
  },
  smartContractDetailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  smartContractDetailLabel: {
    fontSize: 14,
    color: '#64748B',
  },
  smartContractDetailValue: {
    fontSize: 14,
    color: '#333',
  },
  smartContractDetailTotal: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0e4e96',
  },
  approvalModalActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  approvePaymentButton: {
    backgroundColor: '#0e4e96',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    marginRight: 12,
  },
  approvePaymentText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 16,
    marginLeft: 8,
  },
  rejectPaymentButton: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#EF4444',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  rejectPaymentText: {
    color: '#EF4444',
    fontWeight: '500',
    fontSize: 16,
    marginLeft: 8,
  },
  processingStatusContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FEF3C7',
    padding: 12,
    borderRadius: 8,
  },
  processingStatusText: {
    fontSize: 14,
    color: '#92400E',
    marginLeft: 12,
    flex: 1,
  },

  // Wallet Modal
  walletModalContent: {
    padding: 16,
  },
  walletBalanceContainer: {
    alignItems: 'center',
    marginBottom: 24,
  },
  walletBalanceLabel: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 8,
  },
  walletBalanceValue: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#0e4e96',
  },
  walletAddressSection: {
    marginBottom: 16,
  },
  walletAddressLabel: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 8,
  },
  walletAddressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f1f5f9',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  walletAddressValue: {
    fontSize: 14,
    color: '#333',
    fontFamily: 'monospace',
    flex: 1,
  },
  copyButton: {
    padding: 4,
  },
  walletNetworkSection: {
    marginBottom: 24,
  },
  walletNetworkLabel: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 8,
  },
  walletNetworkRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  walletNetworkValue: {
    fontSize: 14,
    color: '#333',
    marginLeft: 8,
  },
  walletActionsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  walletActionButton: {
    alignItems: 'center',
    width: '30%',
  },
  walletActionText: {
    fontSize: 14,
    color: '#0e4e96',
    marginTop: 8,
    textAlign: 'center',
  },
  walletSectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 16,
  },
  walletSecuritySection: {
    marginBottom: 16,
  },
  walletSecurityOption: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e1e1e1',
  },
  walletSecurityOptionContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  walletSecurityOptionText: {
    fontSize: 14,
    color: '#333',
    marginLeft: 12,
  },
});

export default SmartContractPaymentScreen;